import type { Components, Theme } from '@mui/material/styles';

/**
 * Component overrides that adapt to theme mode
 * Uses theme tokens instead of hardcoded colors
 */
export const getComponents = (theme: Theme): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        boxSizing: 'border-box',
      },
      html: {
        scrollBehavior: 'smooth',
      },
      body: {
        margin: 0,
        padding: 0,
      },
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '::-webkit-scrollbar-thumb': {
        background: theme.palette.mode === 'dark' ? '#334155' : '#CBD5E1',
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: theme.palette.mode === 'dark' ? '#475569' : '#94A3B8',
      },
      '::selection': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '0.75rem',
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      contained: {
        boxShadow: theme.shadows[9],
        '&:hover': {
          boxShadow: theme.shadows[10],
          transform: 'translateY(-2px)',
        },
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '1rem',
        border: `1px solid ${theme.palette.divider}`,
        backgroundImage: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor:
            theme.palette.mode === 'dark' ? 'rgba(0, 242, 234, 0.3)' : 'rgba(0, 212, 204, 0.4)',
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[3],
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '9999px',
        fontWeight: 500,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.75rem',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor:
          theme.palette.mode === 'dark' ? 'rgba(11, 17, 32, 0.8)' : 'rgba(248, 250, 252, 0.8)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  },
});

// Default export for use in base theme
export const components: Components<Theme> = {};
